import * as React from "react";
import { FC, useState, useCallback, useMemo } from "react";
import { format } from "lib/globalize-util";
import makeStyles from "@material-ui/core/styles/makeStyles";
import IconButton from "@material-ui/core/IconButton";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import theme from "styles/theme";
import Upload360Dialog from "components/product-media/upload-360-dialog";
import UploadVideoDialog from "components/product-media/upload-video-dialog";
import UploadImageDialog from "components/product-media/upload-image-dialog";
import { IItemRecord } from "records/item/item";
import { DATATEST } from "constants/integration";
import ManageMediaDialog from "components/product-media/manage-media-dialog";
import type { IScope } from "reducers/scope";
import Upload3DDialog from "./upload-3d-dialog";

// prettier-ignore
interface IProps {
    scope: IScope;
    product: IItemRecord;
    classes?: {
        icon?: string;
        button?: string;
        menu?: string;
    };
    /** reload product data when media is uploaded */
    updateProduct? (): void; // prettier-ignore
}

const DIALOG_TYPES = {
    VIDEO: "video",
    IMAGES_360: "360",
    IMAGE: "image",
    THREEDIUM: "threedium",
    MEDIA_IMAGES: "manage-media-images",
};

export const UploadMediaMenu: FC<IProps> = ({ scope, product, updateProduct, classes = {} }) => {
    const { IMAGE, IMAGES_360, MEDIA_IMAGES, VIDEO, THREEDIUM } = DIALOG_TYPES;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [open, setOpen] = useState<string>(null);

    const handleMenu = useCallback((event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget), []);
    const handleMenuClose = useCallback(() => setAnchorEl(null), []);
    const handleOpenDialog = useCallback(
        (type: string) => () => {
            handleMenuClose();
            setOpen(type);
        },
        [handleMenuClose],
    );

    const handleCloseDialog = useCallback(
        (reload?: boolean) => {
            setOpen(null);
            if (reload && updateProduct) {
                updateProduct();
            }
        },
        [updateProduct],
    );

    const useUnlimitedImages = useMemo(() => scope.brand?.getPreference?.("unlimited_images"), [scope]);
    const use3dAssets = useMemo(
        () => useUnlimitedImages && scope.brand?.getPreference?.("use_3d_assets"),
        [scope, useUnlimitedImages],
    );

    // prettier-ignore
    const menuOptions = useMemo(
        () => [
            { display: true, key: IMAGES_360, dataTest: DATATEST.GALLERY.UPLOAD_360_IMAGES, callback: handleOpenDialog(IMAGES_360), label: format("Upload 360 images") },
            { display: true, key: VIDEO, dataTest: DATATEST.GALLERY.UPLOAD_VIDEO, callback: handleOpenDialog(VIDEO), label: format("Upload video") },
            { display: useUnlimitedImages, key: IMAGE, dataTest: DATATEST.GALLERY.UPLOAD_IMAGES, callback: handleOpenDialog(IMAGE), label: format("Upload product images") },
            { display: use3dAssets, key: THREEDIUM, dataTest: DATATEST.GALLERY.UPLOAD_3D, callback: handleOpenDialog(THREEDIUM), label: format("Upload 3D images") },
            { display: useUnlimitedImages, key: MEDIA_IMAGES, dataTest: DATATEST.GALLERY.MANAGE_PRODUCT_MEDIA, callback: handleOpenDialog(MEDIA_IMAGES), label: format("Manage Media/Images") },
        ].filter(option => option.display),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleOpenDialog, useUnlimitedImages]
    );

    const inner = useStyles({});
    return (
        <div>
            <IconButton
                className={classes.button}
                aria-controls="upload-menu"
                data-test={DATATEST.GALLERY.MEDIA_UPLOAD_MENU_BTN}
                onClick={handleMenu}
            >
                <MoreHorizIcon className={classes.icon} />
            </IconButton>
            <Menu
                id="upload-menu"
                className={inner.menu}
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handleMenuClose}
            >
                {menuOptions.map((option) => (
                    <MenuItem
                        key={option.key}
                        className={inner.text}
                        data-test={option.dataTest}
                        onClick={option.callback}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
            {open === IMAGES_360 && <Upload360Dialog product={product} open={true} onClose={handleCloseDialog} />}
            {open === VIDEO && <UploadVideoDialog product={product} open={true} onClose={handleCloseDialog} />}
            {open === IMAGE && <UploadImageDialog product={product} open={true} onClose={handleCloseDialog} />}
            {open === THREEDIUM && <Upload3DDialog product={product} open={true} onClose={handleCloseDialog} />}
            {open === MEDIA_IMAGES && (
                <ManageMediaDialog productId={product._id} open={true} onClose={handleCloseDialog} />
            )}
        </div>
    );
};

const useStyles = makeStyles({
    menu: {
        zIndex: "3000 !important" as any, // not to pass styles directly to Menu component
    },
    text: {
        fontFamily: theme.fontFamily,
        fontSize: 14,
    },
});

export default UploadMediaMenu;
